import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';

import { Container } from '@/components/container';
import { DataGrid, KeenIcon } from '@/components';
import { Tabs, TabsList, Tab, TabPanel } from '@/components/tabs';
import {
  Toolbar,
  ToolbarHeading,
  ToolbarPageTitle,
  ToolbarDescription,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { ConfirmationDialog } from '@/components/dialogBox';
import { selectUserPermissions } from '@/store/authSlice';

import {
  useExecuteAccountingExportMutation,
  useGetAccountingExportEntriesQuery,
  useGetAccountingExportRunQuery,
  // useGetInvoiceExportsQuery,
  useRetryAccountingExportMutation,
} from '@/services/AccountingExportsService';
import {
  AccountingExportEntriesListParams,
  AccountingExportEntryItem,
} from '@/types/accounting-exports.types';
import { AccountingExportRunStatus, PaymentType } from '@/constant';
import { formatFinnishNumber } from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

const AVAILABLE_TABS = [1] as const;

const RUN_STATUS_LABELS: Record<string, string> = {
  [AccountingExportRunStatus.PENDING]: 'Pending',
  [AccountingExportRunStatus.RUNNING]: 'Running',
  [AccountingExportRunStatus.SUCCEEDED]: 'Succeeded',
  [AccountingExportRunStatus.PARTIALLY_SUCCEEDED]: 'Partially Succeeded',
  [AccountingExportRunStatus.FAILED]: 'Failed',
};

const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  [PaymentType.HARBOUR_BERTH]: 'Harbour Berth',
  [PaymentType.HARBOUR_SERVICE]: 'Harbour Service',
  [PaymentType.HARBOUR_ADDON]: 'Harbour Addon',
  [PaymentType.THIRD_PARTY_SERVICE]: 'Third Party Service',
  [PaymentType.SAARISTO_SUBSCRIPTION]: 'Saaristo Subscription',
  [PaymentType.SAARISTO_COMMISSION]: 'Saaristo Commission',
  [PaymentType.CLUB_RESOURCE]: 'Club Resource',
  [PaymentType.CLUB_RESOURCE_ORDER_ITEM]: 'Club Resource Order Item',
  [PaymentType.SERVICE_BOOKING]: 'Service Booking',
  [PaymentType.ADVERTISING]: 'Advertising',
  [PaymentType.OTHER]: 'Other',
  [PaymentType.PENALTY_NO_SHOW]: 'Penalty No Show',
  [PaymentType.PSP_FEE]: 'PSP Fee',
};

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case AccountingExportRunStatus.SUCCEEDED:
      return 'badge-success';
    case AccountingExportRunStatus.PARTIALLY_SUCCEEDED:
    case AccountingExportRunStatus.RUNNING:
      return 'badge-warning';
    case AccountingExportRunStatus.PENDING:
      return 'badge-secondary';
    case AccountingExportRunStatus.FAILED:
      return 'badge-danger';
    default:
      return 'badge-secondary';
  }
};

const AccountingExportDetails = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const userPermissions = useSelector(selectUserPermissions);

  const requestedTab = searchParams.get('activeTab');
  const parsedActiveTab = requestedTab ? Number(requestedTab) : 1;
  const activeTab = AVAILABLE_TABS.includes(parsedActiveTab as 1)
    ? parsedActiveTab
    : 1;

  const handleTabChange = (value: number) => {
    setSearchParams({ activeTab: String(value) }, { replace: true });
  };

  const hasExecutePermission = useMemo(() => {
    return userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('netvisor_accounting_exports_execute')
    );
  }, [userPermissions]);

  const hasRetryPermission = useMemo(() => {
    return userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('netvisor_accounting_exports_retry')
    );
  }, [userPermissions]);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [isExecuteConfirmOpen, setIsExecuteConfirmOpen] = useState(false);
  const [isRetryConfirmOpen, setIsRetryConfirmOpen] = useState(false);

  const {
    data: run,
    isFetching: runLoading,
    error: runError,
  } = useGetAccountingExportRunQuery(id || '', { skip: !id });

  const entriesParams: AccountingExportEntriesListParams | undefined = id
    ? {
        id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      }
    : undefined;

  const {
    data: entries,
    isFetching: entriesLoading,
    error: entriesError,
  } = useGetAccountingExportEntriesQuery(
    entriesParams as AccountingExportEntriesListParams,
    { skip: !entriesParams }
  );

  // const { data: invoiceExports, isFetching: invoiceExportsLoading } =
  //   useGetInvoiceExportsQuery(
  //     {
  //       id: id || '',
  //     },
  //     { skip: !id }
  //   );

  const [executeExport, { isLoading: isExecuting }] =
    useExecuteAccountingExportMutation();
  const [retryExport, { isLoading: isRetrying }] =
    useRetryAccountingExportMutation();

  useEffect(() => {
    if (requestedTab && activeTab !== parsedActiveTab) {
      setSearchParams({ activeTab: String(activeTab) }, { replace: true });
    }
  }, [activeTab, parsedActiveTab, requestedTab, setSearchParams]);

  useEffect(() => {
    const err = (runError || entriesError) as
      | { data?: unknown; error?: string }
      | null
      | undefined;
    if (!err) return;

    let message = 'Failed to fetch accounting export details';
    if (
      err.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err.error === 'string' && err.error) {
      message = err.error;
    }

    enqueueSnackbar(message, { variant: 'error' });
  }, [runError, entriesError, enqueueSnackbar]);

  const isPending = run?.status === AccountingExportRunStatus.PENDING;

  const isFailed = run?.status === AccountingExportRunStatus.FAILED;

  const canExecute = isPending && hasExecutePermission;
  const canRetry = isFailed && hasRetryPermission;

  const handleExecute = async () => {
    if (!id) return;
    try {
      await executeExport(id).unwrap();
      enqueueSnackbar('Export execution started', { variant: 'success' });
      setIsExecuteConfirmOpen(false);
    } catch (e) {
      const err = e as unknown as { data?: unknown; error?: string };
      let message = 'Failed to execute export';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleRetry = async () => {
    if (!id) return;
    try {
      await retryExport(id).unwrap();
      enqueueSnackbar('Export retry started', { variant: 'success' });
      setIsRetryConfirmOpen(false);
    } catch (e) {
      const err = e as unknown as { data?: unknown; error?: string };
      let message = 'Failed to retry export';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const columns: ColumnDef<AccountingExportEntryItem>[] = useMemo(
    () => [
      {
        accessorFn: (row: AccountingExportEntryItem) =>
          formatDisplayDate(row.voucher_date),
        id: 'voucher_date',
        header: 'Voucher Date',
        enableSorting: false,
      },
      {
        accessorFn: (row: AccountingExportEntryItem) => row.voucher_description,
        id: 'voucher_description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-gray-800 contrast:text-white/90">
            {row.original.voucher_description}
          </span>
        ),
      },
      {
        accessorFn: (row: AccountingExportEntryItem) => row.direction,
        id: 'direction',
        header: 'Direction',
        enableSorting: false,
      },
      {
        accessorFn: (row: AccountingExportEntryItem) => row.linesum,
        id: 'linesum',
        header: 'Line sum',
        enableSorting: false,
        cell: ({ row }) => (
          <span>
            {formatFinnishNumber(row.original.linesum, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ),
      },
      {
        accessorFn: (row: AccountingExportEntryItem) => row.currency,
        id: 'currency',
        header: 'Currency',
        enableSorting: false,
      },
      {
        accessorFn: (row: AccountingExportEntryItem) => row.product_type,
        id: 'product_type',
        header: 'Product Type',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm contrast:text-white/90">
            {row.original.product_type
              ? (PAYMENT_TYPE_LABELS[
                  row.original.product_type as PaymentType
                ] ?? row.original.product_type)
              : '—'}
          </span>
        ),
      },
      {
        accessorFn: (row: AccountingExportEntryItem) => row.export_error,
        id: 'export_error',
        header: 'Error',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-700 contrast:text-white/90">
            {row.original.export_error
              ? row.original.export_error.replace(/_/g, ' ')
              : '—'}
          </span>
        ),
      },
    ],
    []
  );

  // const hasAnyInvoiceError = useMemo(() => {
  //   return invoiceExports?.items?.some((item) => !!item.export_error) ?? false;
  // }, [invoiceExports?.items]);

  const pageTitle = run
    ? `Accounting Export Run ${run.id}`
    : 'Accounting Export Run';

  return (
    <>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="page-title" text={pageTitle} />
              <ToolbarDescription>
                View run status and the accounting entries included in the
                export.
              </ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div aria-labelledby="page-title" className="py-6 text-gray-900">
          <div className="mb-4 flex justify-between items-center">
            <button
              type="button"
              onClick={() => navigate('/netvisor/accounting-exports')}
              className="btn btn-sm btn-light gap-2"
            >
              <KeenIcon icon="arrow-left" className="text-lg" />
              Back
            </button>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setIsExecuteConfirmOpen(true)}
                disabled={!canExecute}
                aria-disabled={!canExecute}
              >
                {isExecuting ? 'Executing…' : 'Execute'}
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setIsRetryConfirmOpen(true)}
                disabled={!canRetry}
                aria-disabled={!canRetry}
              >
                {isRetrying ? 'Retrying…' : 'Retry'}
              </button>

              {run?.status === AccountingExportRunStatus.PENDING &&
                (!hasExecutePermission || !hasRetryPermission) && (
                  <p className="text-sm text-gray-600">
                    Some actions are disabled due to missing permissions.
                  </p>
                )}
            </div>
          </div>

          <Tabs defaultValue={activeTab} className="w-full">
            <TabsList className="flex gap-6 mb-5">
              <Tab
                value={1}
                className="text-[14px]"
                onClick={() => handleTabChange(1)}
              >
                Accounting Export
              </Tab>
              {/* <Tab
                value={2}
                className="text-[14px]"
                onClick={() => handleTabChange(2)}
              >
                Invoices
              </Tab> */}
            </TabsList>

            <TabPanel value={1}>
              <section aria-label="Run summary" className="mb-6">
                <div className="rounded-lg border border-gray-200 bg-gray-100 p-4">
                  <h2 className="text-base font-semibold text-gray-900">
                    Summary
                  </h2>

                  {runLoading ? (
                    <p className="mt-3 text-sm text-gray-700">Loading…</p>
                  ) : run ? (
                    <dl className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-700">
                          Status
                        </dt>
                        <dd className="mt-1">
                          <span
                            className={`badge ${getStatusBadgeClass(run.status)}`}
                          >
                            {RUN_STATUS_LABELS[run.status] || run.status}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-700">
                          Period
                        </dt>
                        <dd className="mt-1 text-sm text-gray-800">
                          {formatDisplayDate(run.period_start, '')} -{' '}
                          {formatDisplayDate(run.period_end, '')}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-700">
                          Counts
                        </dt>
                        <dd className="mt-1 text-sm text-gray-800">
                          Entries:{' '}
                          {formatFinnishNumber(run.entry_count, {
                            maximumFractionDigits: 0,
                          })}
                          , Exported:{' '}
                          {formatFinnishNumber(run.exported_entry_count, {
                            maximumFractionDigits: 0,
                          })}
                          , Failed:{' '}
                          {formatFinnishNumber(run.failed_entry_count, {
                            maximumFractionDigits: 0,
                          })}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-sm font-medium text-gray-700">
                          Error
                        </dt>
                        <dd className="mt-1 text-sm text-gray-800 break-words">
                          {run.error_message
                            ? run.error_message.replace(/_/g, ' ')
                            : '—'}
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <p className="mt-3 text-sm text-gray-700">No data found.</p>
                  )}
                </div>
              </section>

              <section aria-label="Accounting entries">
                <div className="rounded-lg border border-gray-200 bg-gray-100">
                  <DataGrid
                    columns={columns}
                    data={entries?.items ?? []}
                    loading={entriesLoading}
                    serverSide={true}
                    pagination={{
                      page:
                        entries?.currentPage !== undefined
                          ? entries.currentPage - 1
                          : pagination.pageIndex,
                      size: entries?.pageLimit ?? pagination.pageSize,
                      totalPages: entries?.totalPages ?? 1,
                      totalItems: entries?.totalItems ?? 0,
                      setPagination,
                    }}
                  />
                </div>
              </section>
            </TabPanel>
          </Tabs>
        </div>
      </Container>

      <ConfirmationDialog
        open={isExecuteConfirmOpen}
        onClose={() => setIsExecuteConfirmOpen(false)}
        onConfirm={handleExecute}
        confirmButtonText="Execute"
        message={
          <p className="text-sm text-gray-700">
            This will execute the selected accounting export run.
          </p>
        }
        disabled={isExecuting}
        isLoading={isExecuting}
      />

      <ConfirmationDialog
        open={isRetryConfirmOpen}
        onClose={() => setIsRetryConfirmOpen(false)}
        onConfirm={handleRetry}
        confirmButtonText="Retry"
        message={
          <p className="text-sm text-gray-700">
            This will retry execution of the selected accounting export run.
          </p>
        }
        disabled={isRetrying}
        isLoading={isRetrying}
      />
    </>
  );
};

export default AccountingExportDetails;
